'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight, FileText, Book, BookOpen } from 'lucide-react';
import { useState } from 'react';

interface SidebarPage {
  slug: string;
  title: string;
  sortOrder?: number;
  children?: SidebarPage[];
}

interface SidebarProps {
  pages: SidebarPage[];
}

function SidebarItem({ page, depth = 0 }: { page: SidebarPage; depth?: number }) {
  const pathname = usePathname();
  const isActive = pathname === `/wiki/${page.slug}`;
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = page.children && page.children.length > 0;
  const isToc = page.slug === 'table-of-contents';
  const isAppendix = page.slug === 'appendices';

  return (
    <div>
      <Link
        href={`/wiki/${page.slug}`}
        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${
          isActive
            ? 'bg-primary/20 text-primary border-l-2 border-primary glow-text'
            : 'hover:bg-muted/50 text-foreground hover:text-primary border-l-2 border-transparent'
        } ${isToc ? 'font-semibold' : ''}`}
        style={{ paddingLeft: `${12 + depth * 16}px` }}
      >
        {hasChildren && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="p-0.5 hover:bg-primary/20 rounded transition-colors"
          >
            <ChevronRight
              className={`h-4 w-4 transition-transform text-primary ${isOpen ? 'rotate-90' : ''}`}
            />
          </button>
        )}
        {isToc ? (
          <BookOpen className="h-4 w-4 text-accent flex-shrink-0" />
        ) : isAppendix ? (
          <Book className="h-4 w-4 text-secondary flex-shrink-0" />
        ) : (
          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        <span className="truncate">{page.title}</span>
      </Link>
      {hasChildren && isOpen && (
        <div className="mt-1">
          {page.children!.map((child) => (
            <SidebarItem key={child.slug} page={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

export function Sidebar({ pages }: SidebarProps) {
  return (
    <aside className="w-72 border-r border-primary/20 bg-card/50 p-4 overflow-y-auto backdrop-blur-sm">
      <h3 className="font-semibold text-xs text-primary uppercase tracking-widest mb-4 flex items-center gap-2" style={{ fontFamily: 'Orbitron, sans-serif' }}>
        <span className="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
        Chapters
      </h3>
      <nav className="space-y-0.5">
        {pages.map((page) => (
          <SidebarItem key={page.slug} page={page} />
        ))}
      </nav>
    </aside>
  );
}
